import React from 'react';

/**
 * Friendly robot illustration for polished empty states (concept mockup).
 * Purely decorative — the accessible message lives in the surrounding
 * EmptyState / section text.
 */
export function RobotIllustration({ className }: { className?: string }) {
  return (
    <svg
      width="96"
      height="96"
      viewBox="0 0 96 96"
      fill="none"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {/* antenna */}
      <line x1="48" y1="18" x2="48" y2="28" stroke="hsl(var(--border-strong))" strokeWidth="2" strokeLinecap="round" />
      <circle cx="48" cy="15" r="3.5" fill="#E10600" opacity="0.9">
        <animate attributeName="opacity" values="0.9;0.4;0.9" dur="2.4s" repeatCount="indefinite" />
      </circle>
      {/* head */}
      <rect x="30" y="28" width="36" height="28" rx="10" fill="hsl(var(--surface-raised))" stroke="hsl(var(--border-strong))" strokeWidth="2" />
      {/* eyes */}
      <circle cx="41" cy="42" r="4" fill="#E10600" />
      <circle cx="55" cy="42" r="4" fill="#E10600" />
      {/* smile */}
      <path d="M42 49 Q48 54 54 49" stroke="hsl(var(--border-strong))" strokeWidth="2" strokeLinecap="round" fill="none" />
      {/* ears */}
      <rect x="25" y="38" width="4" height="9" rx="2" fill="hsl(var(--border-strong))" />
      <rect x="67" y="38" width="4" height="9" rx="2" fill="hsl(var(--border-strong))" />
      {/* body */}
      <rect x="34" y="58" width="28" height="20" rx="8" fill="hsl(var(--surface-raised))" stroke="hsl(var(--border-strong))" strokeWidth="2" />
      {/* chest light */}
      <circle cx="48" cy="68" r="3" fill="#E10600" opacity="0.85" />
      {/* arms */}
      <path d="M34 63 Q26 66 27 73" stroke="hsl(var(--border-strong))" strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M62 63 Q70 66 69 73" stroke="hsl(var(--border-strong))" strokeWidth="2" strokeLinecap="round" fill="none" />
      <circle cx="27" cy="75" r="2.5" fill="hsl(var(--border-strong))" />
      <circle cx="69" cy="75" r="2.5" fill="hsl(var(--border-strong))" />
      {/* ground shadow */}
      <ellipse cx="48" cy="86" rx="20" ry="3" fill="hsl(var(--border))" opacity="0.5" />
    </svg>
  );
}

/**
 * Concept-style empty state: robot + friendly message.
 * role="status" preserved for assistive tech (matches existing EmptyState).
 */
export function RobotEmptyState({ title = 'No items to show', description, action, className }: { title?: string; description?: string; action?: React.ReactNode; className?: string }) {
  return (
    <div className={'flex min-h-32 flex-col items-start justify-center px-5 py-6 text-left ' + (className ?? '')} role="status">
      <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] text-sm text-[hsl(var(--content-subtle))]" aria-hidden="true">—</div>
      <h3 className="text-sm font-semibold text-[hsl(var(--content))]">{title}</h3>
      {description && <p className="mt-1 max-w-md text-sm text-[hsl(var(--content-subtle))]">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
